import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate, formatCurrency, formatNumber } from "@/lib/format";
import { Loader2, FileDown, Check, RotateCcw, Ban, Trash2, Plus, X, Save } from "lucide-react";

export const STATO_PF = {
  bozza: "bg-amber-50 text-amber-700 border-amber-200",
  confermata: "bg-emerald-50 text-emerald-700 border-emerald-200",
  annullata: "bg-slate-100 text-slate-500 border-slate-200",
};

async function download(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const u = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = u;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(u);
}

export default function PrefatturaDetail({ id, onClose, onChanged, canWrite }) {
  const [pf, setPf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!id) { setPf(null); return; }
    api.get(`/prefatture/${id}`).then((r) => { setPf(r.data); setDirty(false); }).catch((e) => toast.error(apiError(e)));
  }, [id]);

  const editable = canWrite && pf?.stato === "bozza";

  const setRiga = (vid, k, v) => {
    setPf((p) => ({ ...p, righe: p.righe.map((r) => (r.viaggio_id === vid ? { ...r, [k]: v } : r)) }));
    setDirty(true);
  };
  const setExtra = (i, k, v) => { setPf((p) => ({ ...p, voci_extra: p.voci_extra.map((e, j) => (j === i ? { ...e, [k]: v } : e)) })); setDirty(true); };

  const action = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      if (data && data.id) setPf(data);
      setDirty(false);
      toast.success(msg);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const salva = () => action(() => api.put(`/prefatture/${pf.id}`, {
    righe: pf.righe.map((r) => ({ viaggio_id: r.viaggio_id, importo_manuale: r.importo_manuale === "" ? null : r.importo_manuale, note: r.note })),
    voci_extra: pf.voci_extra.map((e) => ({ ...e, importo: Number(e.importo || 0) })),
    iva_pct: Number(pf.iva_pct), note: pf.note,
  }), "Prefattura salvata");

  const rimuoviRiga = (vid) => action(() => api.delete(`/prefatture/${pf.id}/righe/${vid}`), "Viaggio rimosso (torna 'eseguito')");
  const conferma = () => action(() => api.post(`/prefatture/${pf.id}/conferma`), "Prefattura confermata");
  const riapri = () => action(() => api.post(`/prefatture/${pf.id}/riapri`), "Prefattura riaperta in bozza");
  const annulla = () => action(() => api.post(`/prefatture/${pf.id}/annulla`), "Prefattura annullata: i viaggi tornano 'eseguiti'");
  const elimina = async () => {
    setBusy(true);
    try {
      await api.delete(`/prefatture/${pf.id}`);
      toast.success("Prefattura eliminata");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const righeTot = pf ? pf.righe.reduce((s, r) => s + Number(r.importo_manuale !== null && r.importo_manuale !== undefined && r.importo_manuale !== "" ? r.importo_manuale : r.importo_calcolato || 0), 0) : 0;
  const extraTot = pf ? pf.voci_extra.reduce((s, e) => s + Number(e.importo || 0), 0) : 0;
  const imponibile = righeTot + extraTot;
  const iva = imponibile * Number(pf?.iva_pct || 0) / 100;

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto" data-testid="prefattura-detail">
        {!pf ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-sky-500" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-3">
                <span data-testid="pf-numero">{pf.numero}</span>
                <Badge variant="outline" className={`capitalize ${STATO_PF[pf.stato]}`} data-testid="pf-stato">{pf.stato}</Badge>
                <span className="text-sm font-normal text-slate-500">{pf.tipo === "attiva" ? "Cliente" : "Sub-vettore"}: <b className="text-slate-800">{pf.soggetto_nome}</b></span>
              </DialogTitle>
              <DialogDescription>Periodo {formatDate(pf.periodo_dal)} → {formatDate(pf.periodo_al)} · {pf.righe.length} viaggi{pf.n_warning ? ` · ${pf.n_warning} avvisi tariffa` : ""}</DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap gap-2 items-center">
              <Button variant="outline" size="sm" onClick={() => download(`/prefatture/${pf.id}/export?format=pdf`, `${pf.numero}.pdf`)} data-testid="pf-export-pdf"><FileDown className="h-4 w-4 mr-1.5" />PDF</Button>
              <Button variant="outline" size="sm" onClick={() => download(`/prefatture/${pf.id}/export?format=xlsx`, `${pf.numero}.xlsx`)} data-testid="pf-export-xlsx"><FileDown className="h-4 w-4 mr-1.5" />Excel</Button>
              <Button variant="outline" size="sm" onClick={() => download(`/prefatture/${pf.id}/export?format=csv`, `${pf.numero}.csv`)} data-testid="pf-export-csv"><FileDown className="h-4 w-4 mr-1.5" />CSV</Button>
              {canWrite && (
                <div className="ml-auto flex gap-2">
                  {pf.stato === "bozza" && <Button size="sm" variant="outline" onClick={salva} disabled={busy || !dirty} data-testid="pf-salva"><Save className="h-4 w-4 mr-1.5" />Salva modifiche</Button>}
                  {pf.stato === "bozza" && <Button size="sm" onClick={conferma} disabled={busy || dirty} data-testid="pf-conferma" title={dirty ? "Salva prima le modifiche" : ""}><Check className="h-4 w-4 mr-1.5" />Conferma</Button>}
                  {pf.stato !== "bozza" && <Button size="sm" variant="outline" onClick={riapri} disabled={busy} data-testid="pf-riapri"><RotateCcw className="h-4 w-4 mr-1.5" />Riapri</Button>}
                  {pf.stato !== "annullata" && <Button size="sm" variant="outline" className="text-red-600" onClick={annulla} disabled={busy} data-testid="pf-annulla"><Ban className="h-4 w-4 mr-1.5" />Annulla</Button>}
                  {pf.stato !== "confermata" && <Button size="sm" variant="ghost" className="text-red-600" onClick={elimina} disabled={busy} data-testid="pf-elimina"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              )}
            </div>

            <div className="border border-slate-200 rounded-md overflow-auto">
              <table className="w-full text-xs" data-testid="pf-righe">
                <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Data</th><th className="text-left px-2 py-1.5">Codice</th><th className="text-left px-2 py-1.5">Tratta</th>
                    <th className="text-left px-2 py-1.5">Prodotto</th><th className="text-left px-2 py-1.5">Targa / Autista</th><th className="text-left px-2 py-1.5">DDT</th>
                    <th className="text-left px-2 py-1.5">Calcolo</th><th className="text-right px-2 py-1.5">Calcolato</th><th className="text-right px-2 py-1.5 w-28">Manuale</th>
                    <th className="text-right px-2 py-1.5">Importo</th>{editable && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {pf.righe.map((r) => {
                    const manual = r.importo_manuale !== null && r.importo_manuale !== undefined && r.importo_manuale !== "";
                    return (
                      <tr key={r.viaggio_id} className="border-t border-slate-100 align-top" data-testid={`pf-riga-${r.viaggio_id}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap">{formatDate(r.data_viaggio)}</td>
                        <td className="px-2 py-1.5">{r.codice_viaggio}</td>
                        <td className="px-2 py-1.5">{r.descrizione}</td>
                        <td className="px-2 py-1.5">{r.prodotto || "—"}</td>
                        <td className="px-2 py-1.5">{[r.targa, r.autista].filter(Boolean).join(" / ") || "—"}</td>
                        <td className="px-2 py-1.5 font-mono">{r.numero_ddt || "—"}</td>
                        <td className="px-2 py-1.5 text-slate-600">
                          {r.metodo && <div>{r.metodo}: {formatNumber(r.quantita, 2)} × {formatNumber(r.prezzo_unitario, 4)}{r.minimo_applicato ? " (min.)" : ""} = {formatCurrency(r.importo_base)}</div>}
                          {r.supplementi?.map((s) => <div key={s.codice}>+ {s.codice} {formatCurrency(s.importo)}</div>)}
                          {r.gasolio_pct ? <div>+ gasolio {formatNumber(r.gasolio_pct, 2)}% {formatCurrency(r.gasolio_importo)}</div> : null}
                          {r.warning && <div className="text-amber-700">⚠ {r.warning}</div>}
                        </td>
                        <td className="px-2 py-1.5 text-right">{formatCurrency(r.importo_calcolato)}</td>
                        <td className="px-2 py-1.5 text-right">
                          {editable ? <Input type="number" step="0.01" className="h-7 text-xs text-right" value={r.importo_manuale ?? ""} onChange={(e) => setRiga(r.viaggio_id, "importo_manuale", e.target.value === "" ? null : Number(e.target.value))} data-testid={`pf-manuale-${r.viaggio_id}`} placeholder="—" /> : (manual ? formatCurrency(r.importo_manuale) : "—")}
                        </td>
                        <td className={`px-2 py-1.5 text-right font-medium ${manual ? "text-sky-700" : ""}`}>{formatCurrency(manual ? r.importo_manuale : r.importo_calcolato)}</td>
                        {editable && <td className="px-1 py-1"><Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={() => rimuoviRiga(r.viaggio_id)} title="Rimuovi dal documento" data-testid={`pf-rimuovi-${r.viaggio_id}`}><X className="h-3.5 w-3.5" /></Button></td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-2">
                <div className="text-xs font-semibold uppercase text-slate-500">Voci manuali (es. aumento gasolio, extra)</div>
                {pf.voci_extra.map((e, i) => (
                  <div key={e.id || i} className="flex gap-2 items-center" data-testid={`pf-extra-${i}`}>
                    <Input className="h-8 text-xs flex-1" value={e.descrizione} onChange={(ev) => setExtra(i, "descrizione", ev.target.value)} placeholder="Descrizione" disabled={!editable} data-testid={`pf-extra-desc-${i}`} />
                    <Input type="number" step="0.01" className="h-8 text-xs w-32 text-right" value={e.importo} onChange={(ev) => setExtra(i, "importo", ev.target.value)} disabled={!editable} data-testid={`pf-extra-importo-${i}`} />
                    {editable && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => { setPf((p) => ({ ...p, voci_extra: p.voci_extra.filter((_, j) => j !== i) })); setDirty(true); }}><X className="h-3.5 w-3.5" /></Button>}
                  </div>
                ))}
                {editable && <Button variant="outline" size="sm" onClick={() => { setPf((p) => ({ ...p, voci_extra: [...p.voci_extra, { descrizione: "", importo: "" }] })); setDirty(true); }} data-testid="pf-extra-add"><Plus className="h-4 w-4 mr-1" />Aggiungi voce</Button>}
                <div className="pt-2">
                  <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Note documento</div>
                  <Textarea rows={2} className="text-xs" value={pf.note || ""} onChange={(e) => { setPf((p) => ({ ...p, note: e.target.value })); setDirty(true); }} disabled={!editable} data-testid="pf-note" />
                </div>
              </div>
              <div className="border border-slate-200 rounded-md p-4 bg-slate-50 text-sm space-y-1.5 self-start">
                <div className="flex justify-between"><span className="text-slate-600">Viaggi</span><span>{formatCurrency(righeTot)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Voci manuali</span><span>{formatCurrency(extraTot)}</span></div>
                <div className="flex justify-between font-medium border-t pt-1.5"><span>Imponibile</span><span data-testid="pf-imponibile">{formatCurrency(imponibile)}</span></div>
                <div className="flex justify-between items-center"><span className="text-slate-600 flex items-center gap-1">IVA <Input type="number" step="1" className="h-6 w-16 text-xs inline-block" value={pf.iva_pct} onChange={(e) => { setPf((p) => ({ ...p, iva_pct: e.target.value })); setDirty(true); }} disabled={!editable} data-testid="pf-iva" />%</span><span>{formatCurrency(iva)}</span></div>
                <div className="flex justify-between text-base font-head font-bold border-t pt-2"><span>Totale</span><span data-testid="pf-totale">{formatCurrency(imponibile + iva)}</span></div>
                {dirty && <div className="text-xs text-amber-700 pt-1">Modifiche non salvate</div>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
